import { defineMessages } from 'react-intl';

import type { FreightPackagingType } from '../../../../types/ltl';
import type { MessageDescriptor } from 'react-intl';

export const handlingUnitsStepMessages = defineMessages({
  handlingUnitTitle: {
    defaultMessage: 'Handling Unit {number}',
    id: 'E7qa3x',
    description: 'Title for a handling unit card in the booking wizard',
  },
  handlingUnitTitleSingle: {
    defaultMessage: 'Handling Unit',
    id: 'bZv9mC',
    description: 'Title for a handling unit card when it is the only handling unit',
  },
  removeHandlingUnit: {
    defaultMessage: 'Remove',
    id: 'eZ6Jum',
    description: 'Button to remove a handling unit',
  },
  quantityLabel: {
    defaultMessage: 'Quantity',
    id: 'Xs1BCX',
    description: 'Label for the handling unit quantity input',
  },
  unitTypeLabel: {
    defaultMessage: 'Unit Type',
    id: 'bYlDFT',
    description: 'Label for the handling unit packaging type dropdown',
  },
  stackableLabel: {
    defaultMessage: 'Stackable',
    id: 'sm2f90',
    description: 'Label for the handling unit stackable checkbox',
  },
  addHandlingUnit: {
    defaultMessage: '+ Add Handling Unit',
    id: '1C6Kso',
    description: 'Button to add another handling unit',
  },
  commodityTitleSingle: {
    defaultMessage: 'Commodity',
    id: 'xuS14w',
    description: 'Title for a commodity card when it is the only commodity in its handling unit',
  },
  commodityTitle: {
    defaultMessage: 'Commodity {number}',
    id: '52VqGa',
    description: 'Title for a commodity card within a handling unit',
  },
  productNameLabel: {
    defaultMessage: 'Commodity Search',
    id: 'pYsIm2',
    description: 'Label for the commodity product name search field',
  },
  productNamePlaceholder: {
    defaultMessage: 'Search',
    id: 'siMzmi',
    description: 'Placeholder for the commodity product name search field',
  },
  totalWeightLabel: {
    defaultMessage: 'Total Weight',
    id: 'EyGNAj',
    description: 'Label for the commodity total weight input',
  },
  classLabel: {
    defaultMessage: 'Class',
    id: 'kHPbO1',
    description: 'Label for the commodity freight class dropdown',
  },
  classPlaceholder: {
    defaultMessage: 'Select class',
    id: 'G0aUxn',
    description: 'Placeholder for the commodity freight class dropdown',
  },
  densityResult: {
    defaultMessage: 'Density: {density} lb/ft³',
    id: 'iqDzFK',
    description: 'Displays the computed commodity density',
  },
  densityNeedsDimensions: {
    defaultMessage: 'Enter dimensions and weight to calculate density',
    id: 'KEYu2V',
    description: 'Hint shown when density cannot be computed because dimensions or weight are missing',
  },
  showCommodityDetails: {
    defaultMessage: 'Show commodity details',
    id: 'muTpg+',
    description: 'Link to reveal the optional commodity detail fields',
  },
  hideCommodityDetails: {
    defaultMessage: 'Hide commodity details',
    id: '/wijIw',
    description: 'Link to hide the optional commodity detail fields',
  },
  addCommodity: {
    defaultMessage: '+ Add Commodity',
    id: 'W1ReiF',
    description: 'Button to add another commodity to a handling unit',
  },
  removeCommodity: {
    defaultMessage: 'Remove',
    id: '25UtUh',
    description: 'Button to remove a commodity from a handling unit',
  },
  nmfcCodeLabel: {
    defaultMessage: 'NMFC Code',
    id: 'jxPsGZ',
    description: 'Label for the optional NMFC code field',
  },
  nmfcCodePlaceholder: {
    defaultMessage: 'Enter NMFC Code',
    id: 'I5+nwB',
    description: 'Placeholder for the NMFC code field',
  },
  pieceLabel: {
    defaultMessage: 'Piece',
    id: 'fXDXYC',
    description: 'Label for the optional piece type dropdown',
  },
  piecePlaceholder: {
    defaultMessage: 'Select piece type',
    id: '9HEjXy',
    description: 'Placeholder for the piece type dropdown',
  },
  numberOfPiecesLabel: {
    defaultMessage: 'Number of Pieces',
    id: 'Cj//Rt',
    description: 'Label for the optional number of pieces input',
  },
  commodityDescriptionLabel: {
    defaultMessage: 'Commodity Description',
    id: 'yC8H8o',
    description: 'Label for the optional commodity description field',
  },
  commodityDescriptionPlaceholder: {
    defaultMessage: 'Enter description',
    id: 'xEnya9',
    description: 'Placeholder for the commodity description field',
  },
  hazmatCheckboxLabel: {
    defaultMessage: 'Contains hazardous material',
    id: 'kTF9C1',
    description: 'Checkbox label that reveals the hazmat info fields for a commodity when checked',
  },
  hazmatIdNumberLabel: {
    defaultMessage: 'UN / NA Number',
    id: 'pxViqs',
    description: 'Label for the UN or NA identification number field in hazmat info',
  },
  hazmatProperShippingNameLabel: {
    defaultMessage: 'Proper Shipping Name',
    id: '16Rwny',
    description: 'Label for the proper shipping name field in hazmat info',
  },
  hazmatHazardClassLabel: {
    defaultMessage: 'Hazard Class',
    id: 'IxD55K',
    description: 'Label for the hazard class dropdown in hazmat info',
  },
  hazmatHazardClassPlaceholder: {
    defaultMessage: 'Select hazard class',
    id: 'B8rsWL',
    description: 'Placeholder for the hazard class dropdown in hazmat info',
  },
  hazmatPackingGroupLabel: {
    defaultMessage: 'Packing Group',
    id: 'thoy9c',
    description: 'Label for the packing group dropdown in hazmat info',
  },
  hazmatPackingGroupPlaceholder: {
    defaultMessage: 'Select packing group',
    id: 'ZTNd8M',
    description: 'Placeholder for the packing group dropdown in hazmat info',
  },
  hazmatNoPackagingGroupOption: {
    defaultMessage: 'No Packaging Group',
    id: 'NU9jNI',
    description: 'Label for the "no packaging group" option in the packing group dropdown in hazmat info',
  },
  hazmatEmergencyContactNameLabel: {
    defaultMessage: 'Emergency Contact Name',
    id: 'vk2Gv8',
    description: 'Label for the emergency contact name field in hazmat info',
  },
  hazmatEmergencyPhoneLabel: {
    defaultMessage: 'Emergency Phone Number',
    id: 'GgW7fB',
    description: 'Label for the emergency phone number field in hazmat info',
  },
  hazmatErgReferenceLabel: {
    defaultMessage: 'ERG Reference No.',
    id: '274UNk',
    description: 'Label for the ERG reference number field in hazmat info',
  },
  hazmatFlashpointLabel: {
    defaultMessage: 'Flashpoint Temperature (F)',
    id: 'ZMEkoj',
    description: 'Label for the flashpoint temperature field in hazmat info',
  },
  hazmatAdditionalDetailsLabel: {
    defaultMessage: 'Additional Details',
    id: 'gBmHwK',
    description: 'Label for the additional details field in hazmat info',
  },
  hazmatAdditionalDetailsPlaceholder: {
    defaultMessage: 'Enter additional details',
    id: 'nvv1IO',
    description: 'Placeholder for the additional details textarea in hazmat info',
  },
  handlingUnitPresetLabel: {
    defaultMessage: 'Handling Unit Preset',
    id: 'ACnQPH',
    description: 'Label for the handling unit preset autocomplete search field in the wizard',
  },
  handlingUnitPresetPlaceholder: {
    defaultMessage: 'Select or search presets',
    id: 'GDEsgE',
    description: 'Placeholder for the handling unit preset dropdown/search field',
  },
  handlingUnitPresetNothingFound: {
    defaultMessage: 'No presets found',
    id: 'U7SEcr',
    description: 'Shown in the handling unit preset dropdown when no presets match the search or the merchant has none',
  },
  handlingSectionLabel: {
    defaultMessage: 'Handling Units',
    id: '7AurAj',
    description: 'Section label shown in the completed handling-units step summary',
  },
  summary: {
    defaultMessage: '{count, plural, one {# handling unit} other {# handling units}}, {weight} {unit} total',
    id: 'y1SyzZ',
    description: 'Compact summary of handling units count and total weight',
  },
  summaryFooterLabel: {
    defaultMessage: 'Summary',
    id: 'wrASv2',
    description: 'Label for the handling unit card summary footer',
  },
  summaryFooterText: {
    defaultMessage: 'Total weight is {weight} {unit} across {quantityAndType}',
    id: 'OTn1Gq',
    description: 'Text of the handling unit card summary footer with total weight, quantity and packaging type',
  },
  quantityBag: {
    defaultMessage: '{count, plural, one {# bag} other {# bags}}',
    id: '0JayOn',
    description: 'Quantity of handling units with the Bag packaging type, used in the handling unit subtitle',
  },
  quantityBale: {
    defaultMessage: '{count, plural, one {# bale} other {# bales}}',
    id: 'l7V29N',
    description: 'Quantity of handling units with the Bale packaging type, used in the handling unit subtitle',
  },
  quantityBox: {
    defaultMessage: '{count, plural, one {# box} other {# boxes}}',
    id: '83iQ2e',
    description: 'Quantity of handling units with the Box packaging type, used in the handling unit subtitle',
  },
  quantityBundle: {
    defaultMessage: '{count, plural, one {# bundle} other {# bundles}}',
    id: 'ws7vsK',
    description: 'Quantity of handling units with the Bundle packaging type, used in the handling unit subtitle',
  },
  quantityCarton: {
    defaultMessage: '{count, plural, one {# carton} other {# cartons}}',
    id: '151yFG',
    description: 'Quantity of handling units with the Carton packaging type, used in the handling unit subtitle',
  },
  quantityCase: {
    defaultMessage: '{count, plural, one {# case} other {# cases}}',
    id: 'IGExsW',
    description: 'Quantity of handling units with the Case packaging type, used in the handling unit subtitle',
  },
  quantityCrate: {
    defaultMessage: '{count, plural, one {# crate} other {# crates}}',
    id: 'CwNKxG',
    description: 'Quantity of handling units with the Crate packaging type, used in the handling unit subtitle',
  },
  quantityCylinder: {
    defaultMessage: '{count, plural, one {# cylinder} other {# cylinders}}',
    id: 'WsEpfj',
    description: 'Quantity of handling units with the Cylinder packaging type, used in the handling unit subtitle',
  },
  quantityDrum: {
    defaultMessage: '{count, plural, one {# drum} other {# drums}}',
    id: 'ayZYfj',
    description: 'Quantity of handling units with the Drum packaging type, used in the handling unit subtitle',
  },
  quantityPail: {
    defaultMessage: '{count, plural, one {# pail} other {# pails}}',
    id: 'd9sv8P',
    description: 'Quantity of handling units with the Pail packaging type, used in the handling unit subtitle',
  },
  quantityPallet: {
    defaultMessage: '{count, plural, one {# pallet} other {# pallets}}',
    id: '6PWhoH',
    description: 'Quantity of handling units with the Pallet packaging type, used in the handling unit subtitle',
  },
  quantityPieces: {
    defaultMessage: '{count, plural, one {# piece} other {# pieces}}',
    id: 'EyKdL8',
    description: 'Quantity of handling units with the Pieces packaging type, used in the handling unit subtitle',
  },
  quantityReel: {
    defaultMessage: '{count, plural, one {# reel} other {# reels}}',
    id: 'IebVeD',
    description: 'Quantity of handling units with the Reel packaging type, used in the handling unit subtitle',
  },
  quantityRoll: {
    defaultMessage: '{count, plural, one {# roll} other {# rolls}}',
    id: 'QMIvH4',
    description: 'Quantity of handling units with the Roll packaging type, used in the handling unit subtitle',
  },
  quantitySkid: {
    defaultMessage: '{count, plural, one {# skid} other {# skids}}',
    id: 'V5kh4Z',
    description: 'Quantity of handling units with the Skid packaging type, used in the handling unit subtitle',
  },
  quantityTank: {
    defaultMessage: '{count, plural, one {# tank} other {# tanks}}',
    id: 'RS9Etm',
    description: 'Quantity of handling units with the Tank packaging type, used in the handling unit subtitle',
  },
  quantityTote: {
    defaultMessage: '{count, plural, one {# tote} other {# totes}}',
    id: 'QmB+oG',
    description: 'Quantity of handling units with the Tote packaging type, used in the handling unit subtitle',
  },
  quantityTrailer: {
    defaultMessage: '{count, plural, one {# trailer} other {# trailers}}',
    id: 'gSbys8',
    description: 'Quantity of handling units with the Trailer packaging type, used in the handling unit subtitle',
  },
  quantityTube: {
    defaultMessage: '{count, plural, one {# tube} other {# tubes}}',
    id: 'fsNPN5',
    description: 'Quantity of handling units with the Tube packaging type, used in the handling unit subtitle',
  },
});

export const handlingUnitQuantityMessages: Record<FreightPackagingType, MessageDescriptor> = {
  Bag: handlingUnitsStepMessages.quantityBag,
  Bale: handlingUnitsStepMessages.quantityBale,
  Box: handlingUnitsStepMessages.quantityBox,
  Bundle: handlingUnitsStepMessages.quantityBundle,
  Carton: handlingUnitsStepMessages.quantityCarton,
  Case: handlingUnitsStepMessages.quantityCase,
  Crate: handlingUnitsStepMessages.quantityCrate,
  Cylinder: handlingUnitsStepMessages.quantityCylinder,
  Drum: handlingUnitsStepMessages.quantityDrum,
  Pail: handlingUnitsStepMessages.quantityPail,
  Pallet: handlingUnitsStepMessages.quantityPallet,
  Pieces: handlingUnitsStepMessages.quantityPieces,
  Reel: handlingUnitsStepMessages.quantityReel,
  Roll: handlingUnitsStepMessages.quantityRoll,
  Skid: handlingUnitsStepMessages.quantitySkid,
  Tank: handlingUnitsStepMessages.quantityTank,
  Tote: handlingUnitsStepMessages.quantityTote,
  Trailer: handlingUnitsStepMessages.quantityTrailer,
  Tube: handlingUnitsStepMessages.quantityTube,
};
